import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  SimpleGrid,
  Flex,
  Icon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  useColorMode,
  Input,
  InputGroup,
  InputLeftAddon,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Select,
} from "@chakra-ui/react";
import {
  ChevronRightIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import {
  FaCalculator,
  FaChartPie,
  FaDollarSign,
  FaUsers,
  FaCoins,
  FaHandHoldingUsd,
  FaChartLine,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getBrandValue } from "../../brandConfig";

const TithingCalculator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode } = useColorMode();

  // Get business from navigation state or use default
  const businessFromState = location.state?.business;

  const [monthlyProfit, setMonthlyProfit] = useState(businessFromState?.monthlyProfit || 50000);
  const [tithingPercentage, setTithingPercentage] = useState(20);
  const [yourTokens, setYourTokens] = useState(businessFromState ? 10000 : 25000);
  const [totalSupply, setTotalSupply] = useState(businessFromState?.totalSupply || 1000000);
  const [poolSize, setPoolSize] = useState(20); // Number of investors in the pool

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 248, 248, 0.9) 50%, rgba(255, 255, 255, 0.95) 100%)`
      : `linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)`;
  const cardBorder =
    colorMode === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)";
  const textPrimary = getColor(
    colorMode === "light" ? "text.primary" : "text.primaryDark",
    colorMode
  );
  const textSecondary = getColor(
    colorMode === "light" ? "text.secondary" : "text.secondaryDark",
    colorMode
  );
  const textMuted = getColor(
    colorMode === "light" ? "text.muted" : "text.mutedDark",
    colorMode
  );

  // Calculations
  const tithingAmount = monthlyProfit * (tithingPercentage / 100);
  const ownershipPercentage = (yourTokens / totalSupply) * 100;
  const monthlyPayout = (yourTokens / totalSupply) * tithingAmount;
  const annualPayout = monthlyPayout * 12;

  // Pool calculations (assuming equal distribution among pool members)
  const tokensPerPoolMember = totalSupply / poolSize;
  const poolMemberPayout = (tokensPerPoolMember / totalSupply) * tithingAmount;

  // ROI calculation (assuming token price of $2 for simplicity)
  const assumedTokenPrice = 2;
  const investment = yourTokens * assumedTokenPrice;
  const annualROI = investment > 0 ? ((annualPayout / investment) * 100).toFixed(2) : "0";

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="1400px" py={16}>
        <VStack spacing={16} align="stretch">
          {/* Hero */}
          <Box textAlign="center">
            <Badge colorScheme="green" fontSize="md" px={4} py={2} mb={4}>
              Tithing Calculator
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "6xl" }}
              fontWeight="800"
              mb={6}
              color={textPrimary}
              bgGradient="linear(to-r, #10B981, #059669)"
              bgClip="text"
            >
              20% Cashflow Distribution
            </Heading>
            <Text fontSize="xl" color={textSecondary} maxW="900px" mx="auto">
              Calculate your monthly and annual returns based on business profit
              tithing and your token ownership percentage.
            </Text>
          </Box>

          {/* Quick Stats */}
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
            <Card
              bg="linear-gradient(135deg, #10B981 0%, #059669 100%)"
              color="white"
              boxShadow="0 8px 24px rgba(16, 185, 129, 0.3)"
            >
              <CardBody p={6}>
                <Stat>
                  <StatLabel fontSize="sm" opacity={0.9}>
                    Your Monthly Payout
                  </StatLabel>
                  <StatNumber fontSize="3xl">
                    ${monthlyPayout.toFixed(2)}
                  </StatNumber>
                  <StatHelpText fontSize="sm">
                    Based on {ownershipPercentage.toFixed(2)}% ownership
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card
              bg="linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)"
              color="white"
              boxShadow="0 8px 24px rgba(59, 130, 246, 0.3)"
            >
              <CardBody p={6}>
                <Stat>
                  <StatLabel fontSize="sm" opacity={0.9}>
                    Annual Payout
                  </StatLabel>
                  <StatNumber fontSize="3xl">
                    ${annualPayout.toFixed(2)}
                  </StatNumber>
                  <StatHelpText fontSize="sm">
                    12 months @ current profit
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card
              bg="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
              color="white"
              boxShadow="0 8px 24px rgba(245, 158, 11, 0.3)"
            >
              <CardBody p={6}>
                <Stat>
                  <StatLabel fontSize="sm" opacity={0.9}>
                    Total Tithing
                  </StatLabel>
                  <StatNumber fontSize="3xl">
                    ${tithingAmount.toFixed(0)}
                  </StatNumber>
                  <StatHelpText fontSize="sm">
                    {tithingPercentage}% of ${monthlyProfit.toLocaleString()} profit
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card
              bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)"
              color="white"
              boxShadow="0 8px 24px rgba(147, 51, 234, 0.3)"
            >
              <CardBody p={6}>
                <Stat>
                  <StatLabel fontSize="sm" opacity={0.9}>
                    Annual ROI
                  </StatLabel>
                  <StatNumber fontSize="3xl">
                    {annualROI}%
                  </StatNumber>
                  <StatHelpText fontSize="sm">
                    On ${investment.toLocaleString()} investment
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Input Controls */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
              <Flex align="center">
                <Icon as={FaCalculator} w={7} h={7} color="#10B981" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  Customize Your Scenario
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                {/* Left Column */}
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="sm" mb={4} color={textPrimary}>
                      Business Metrics
                    </Heading>

                    <Box mb={6}>
                      <Text fontSize="sm" color={textSecondary} mb={2}>
                        Monthly Profit (After Expenses)
                      </Text>
                      <InputGroup size="lg">
                        <InputLeftAddon>$</InputLeftAddon>
                        <Input
                          type="number"
                          value={monthlyProfit}
                          onChange={(e) => setMonthlyProfit(Number(e.target.value))}
                        />
                      </InputGroup>
                      <Slider
                        value={monthlyProfit}
                        min={5000}
                        max={200000}
                        step={5000}
                        onChange={(val) => setMonthlyProfit(val)}
                        mt={3}
                      >
                        <SliderTrack bg="gray.200">
                          <SliderFilledTrack bg="#10B981" />
                        </SliderTrack>
                        <SliderThumb boxSize={6} bg="#10B981" />
                      </Slider>
                      <Flex justify="space-between" fontSize="xs" color={textMuted} mt={1}>
                        <Text>$5K</Text>
                        <Text>$200K</Text>
                      </Flex>
                    </Box>

                    <Box mb={6}>
                      <Text fontSize="sm" color={textSecondary} mb={2}>
                        Tithing Percentage (% of Profit Distributed)
                      </Text>
                      <Select
                        size="lg"
                        value={tithingPercentage}
                        onChange={(e) => setTithingPercentage(Number(e.target.value))}
                      >
                        <option value={10}>10% - Conservative</option>
                        <option value={15}>15% - Moderate</option>
                        <option value={20}>20% - Standard (Recommended)</option>
                        <option value={25}>25% - Generous</option>
                        <option value={30}>30% - Maximum</option>
                      </Select>
                    </Box>

                    <Box>
                      <Text fontSize="sm" color={textSecondary} mb={2}>
                        Total Token Supply
                      </Text>
                      <InputGroup size="lg">
                        <Input
                          type="number"
                          value={totalSupply}
                          onChange={(e) => setTotalSupply(Number(e.target.value))}
                        />
                        <InputLeftAddon>tokens</InputLeftAddon>
                      </InputGroup>
                    </Box>
                  </Box>
                </VStack>

                {/* Right Column */}
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="sm" mb={4} color={textPrimary}>
                      Your Investment
                    </Heading>

                    <Box mb={6}>
                      <Text fontSize="sm" color={textSecondary} mb={2}>
                        Your Token Holdings
                      </Text>
                      <InputGroup size="lg">
                        <Input
                          type="number"
                          value={yourTokens}
                          onChange={(e) => setYourTokens(Number(e.target.value))}
                        />
                        <InputLeftAddon>tokens</InputLeftAddon>
                      </InputGroup>
                      <Slider
                        value={yourTokens}
                        min={1000}
                        max={Math.min(totalSupply / 2, 500000)}
                        step={1000}
                        onChange={(val) => setYourTokens(val)}
                        mt={3}
                      >
                        <SliderTrack bg="gray.200">
                          <SliderFilledTrack bg="#9333EA" />
                        </SliderTrack>
                        <SliderThumb boxSize={6} bg="#9333EA" />
                      </Slider>
                      <Text fontSize="xs" color={textMuted} mt={1}>
                        Ownership: {ownershipPercentage.toFixed(2)}%
                      </Text>
                    </Box>

                    <Box
                      p={5}
                      bg="rgba(147, 51, 234, 0.1)"
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor="rgba(147, 51, 234, 0.3)"
                    >
                      <Heading size="xs" mb={3} color={textPrimary}>
                        Ownership Breakdown
                      </Heading>
                      <Progress
                        value={ownershipPercentage}
                        size="lg"
                        colorScheme="purple"
                        borderRadius="full"
                        mb={2}
                      />
                      <Flex justify="space-between" fontSize="sm">
                        <Text color={textSecondary}>
                          Your share: {yourTokens.toLocaleString()} tokens
                        </Text>
                        <Text color="#9333EA" fontWeight="600">
                          {ownershipPercentage.toFixed(2)}%
                        </Text>
                      </Flex>
                    </Box>
                  </Box>
                </VStack>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Pool Distribution (20 Investor Model) */}
          <Card
            bg="linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.1))"
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px rgba(59, 130, 246, 0.3)"
            borderRadius="2xl"
            borderWidth="2px"
            borderColor="rgba(59, 130, 246, 0.4)"
          >
            <CardHeader
              bg="rgba(59, 130, 246, 0.2)"
              borderBottomWidth="1px"
              borderColor="rgba(59, 130, 246, 0.3)"
            >
              <Flex align="center" justify="space-between">
                <Flex align="center">
                  <Icon as={FaUsers} w={7} h={7} color="#3B82F6" mr={4} />
                  <Heading size="lg" color={textPrimary}>
                    Pool Distribution Model
                  </Heading>
                </Flex>
                <InputGroup maxW="200px">
                  <InputLeftAddon fontSize="sm">Pool Size</InputLeftAddon>
                  <Input
                    type="number"
                    value={poolSize}
                    onChange={(e) => setPoolSize(Number(e.target.value))}
                    min={2}
                    max={100}
                  />
                </InputGroup>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <Text fontSize="lg" mb={6} color={textSecondary} textAlign="center">
                If tokens are distributed equally among {poolSize} investors in the pool,
                each would receive the following monthly payouts:
              </Text>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
                <Box
                  p={6}
                  bg="rgba(255, 255, 255, 0.1)"
                  borderRadius="lg"
                  textAlign="center"
                >
                  <Text fontSize="sm" color={textSecondary} mb={2}>
                    Tokens per Member
                  </Text>
                  <Text fontSize="3xl" fontWeight="700" color="#3B82F6">
                    {tokensPerPoolMember.toLocaleString()}
                  </Text>
                  <Text fontSize="xs" color={textMuted}>
                    {((tokensPerPoolMember / totalSupply) * 100).toFixed(2)}% ownership each
                  </Text>
                </Box>
                <Box
                  p={6}
                  bg="rgba(255, 255, 255, 0.1)"
                  borderRadius="lg"
                  textAlign="center"
                >
                  <Text fontSize="sm" color={textSecondary} mb={2}>
                    Monthly Payout per Member
                  </Text>
                  <Text fontSize="3xl" fontWeight="700" color="#10B981">
                    ${poolMemberPayout.toFixed(2)}
                  </Text>
                  <Text fontSize="xs" color={textMuted}>
                    Based on {tithingPercentage}% tithing
                  </Text>
                </Box>
                <Box
                  p={6}
                  bg="rgba(255, 255, 255, 0.1)"
                  borderRadius="lg"
                  textAlign="center"
                >
                  <Text fontSize="sm" color={textSecondary} mb={2}>
                    Annual per Member
                  </Text>
                  <Text fontSize="3xl" fontWeight="700" color="#F59E0B">
                    ${(poolMemberPayout * 12).toFixed(2)}
                  </Text>
                  <Text fontSize="xs" color={textMuted}>
                    12 months
                  </Text>
                </Box>
              </SimpleGrid>

              <Heading size="md" mb={4} color={textPrimary} textAlign="center">
                Pool Payout Scenarios
              </Heading>
              <Table variant="simple" size="md">
                <Thead>
                  <Tr>
                    <Th color={textSecondary}>Monthly Profit</Th>
                    <Th color={textSecondary} isNumeric>
                      Total Tithing (20%)
                    </Th>
                    <Th color={textSecondary} isNumeric>
                      Per Member/Month
                    </Th>
                    <Th color={textSecondary} isNumeric>
                      Per Member/Year
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[10000, 25000, 50000, 100000, 200000].map((profit) => {
                    const tithing = profit * (tithingPercentage / 100);
                    const perMember = tithing / poolSize;
                    const perYear = perMember * 12;
                    return (
                      <Tr
                        key={profit}
                        bg={profit === monthlyProfit ? "rgba(59, 130, 246, 0.1)" : "transparent"}
                      >
                        <Td fontWeight="600" color={textPrimary}>
                          ${profit.toLocaleString()}
                        </Td>
                        <Td isNumeric color="#F59E0B" fontWeight="600">
                          ${tithing.toLocaleString()}
                        </Td>
                        <Td isNumeric color="#10B981" fontWeight="700" fontSize="lg">
                          ${perMember.toFixed(2)}
                        </Td>
                        <Td isNumeric color="#9333EA" fontWeight="600">
                          ${perYear.toFixed(2)}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </CardBody>
          </Card>

          {/* How It Works */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
              <Flex align="center">
                <Icon as={FaChartPie} w={7} h={7} color="#9333EA" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  How Tithing Works
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box>
                  <Heading size="md" mb={4} color={textPrimary}>
                    The 20% Standard
                  </Heading>
                  <Text fontSize="md" mb={4} color={textSecondary} lineHeight="1.8">
                    The Business Builder platform recommends a <strong>20% profit tithing
                    model</strong> where businesses distribute 20% of their monthly profit
                    to token holders proportionally.
                  </Text>
                  <Text fontSize="md" mb={4} color={textSecondary} lineHeight="1.8">
                    This creates aligned incentives: token holders want the business to
                    be profitable, and businesses get capital from investors who are
                    committed to long-term success.
                  </Text>
                  <Box
                    p={4}
                    bg="rgba(16, 185, 129, 0.1)"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="rgba(16, 185, 129, 0.3)"
                  >
                    <Text fontSize="sm" fontWeight="600" color="#10B981" mb={2}>
                      Example Calculation:
                    </Text>
                    <Text fontSize="sm" color={textSecondary}>
                      Business profit: ${monthlyProfit.toLocaleString()}/month
                      <br />
                      20% tithing: ${tithingAmount.toLocaleString()}/month distributed
                      <br />
                      Your {ownershipPercentage.toFixed(2)}% ownership = ${monthlyPayout.toFixed(2)}/month
                    </Text>
                  </Box>
                </Box>

                <Box>
                  <Heading size="md" mb={4} color={textPrimary}>
                    Why This Model Works
                  </Heading>
                  <VStack spacing={3} align="stretch">
                    {[
                      {
                        icon: FaDollarSign,
                        title: "Transparent Revenue Sharing",
                        desc: "All calculations are on-chain and verifiable",
                        color: "#10B981",
                      },
                      {
                        icon: FaHandHoldingUsd,
                        title: "Monthly Cashflow",
                        desc: "Regular distributions, not speculation",
                        color: "#3B82F6",
                      },
                      {
                        icon: FaChartLine,
                        title: "Aligned Incentives",
                        desc: "Token holders succeed when business succeeds",
                        color: "#F59E0B",
                      },
                      {
                        icon: FaUsers,
                        title: "Community Building",
                        desc: "Investors become advocates and supporters",
                        color: "#9333EA",
                      },
                    ].map((item, index) => (
                      <Flex
                        key={index}
                        p={4}
                        bg={`${item.color}20`}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={`${item.color}40`}
                        align="center"
                      >
                        <Icon as={item.icon} w={8} h={8} color={item.color} mr={4} />
                        <Box>
                          <Text fontWeight="600" color={textPrimary} mb={1}>
                            {item.title}
                          </Text>
                          <Text fontSize="sm" color={textSecondary}>
                            {item.desc}
                          </Text>
                        </Box>
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* CTA */}
          <Box
            textAlign="center"
            bg="linear-gradient(135deg, #10B981 0%, #059669 100%)"
            p={12}
            borderRadius="2xl"
            color="white"
          >
            <Icon as={FaCoins} w={12} h={12} mb={4} />
            <Heading size="2xl" mb={4}>
              Ready to Invest?
            </Heading>
            <Text fontSize="xl" mb={8} maxW="700px" mx="auto">
              Browse tokenized businesses on the marketplace and start receiving
              monthly profit distributions.
            </Text>
            <Flex justify="center" gap={4} flexWrap="wrap">
              <Button
                size="lg"
                bg="white"
                color="#10B981"
                rightIcon={<ChevronRightIcon />}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(255, 255, 255, 0.3)",
                }}
                px={10}
                py={7}
                fontSize="xl"
                onClick={() => navigate("/businessbuilder/marketplace")}
              >
                Browse Marketplace
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderWidth="2px"
                borderColor="white"
                color="white"
                _hover={{
                  bg: "rgba(255, 255, 255, 0.1)",
                }}
                px={10}
                py={7}
                fontSize="xl"
                onClick={() => navigate("/businessbuilder/tokenomics")}
              >
                Learn About Tokenomics
              </Button>
            </Flex>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default TithingCalculator;
